"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"

const STORAGE_KEY = "mmi_cookie_consent" // "granted" | "denied"

function updateConsent(status: "granted" | "denied") {
  window.dataLayer = window.dataLayer || []
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: status,
      ad_storage: status,
      ad_user_data: status,
      ad_personalization: status,
    })
  } else {
    // Fallback in case gtag() hasn't been defined by the head snippet yet
    window.dataLayer.push([
      "consent",
      "update",
      {
        analytics_storage: status,
        ad_storage: status,
        ad_user_data: status,
        ad_personalization: status,
      },
    ])
  }
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const saved = window.localStorage.getItem(STORAGE_KEY)
    if (saved === "granted" || saved === "denied") {
      // Re-apply the visitor's previous choice on every page load,
      // since Consent Mode defaults reset to "denied" on each load.
      updateConsent(saved)
    } else {
      setVisible(true)
    }
  }, [])

  const handleChoice = (status: "granted" | "denied") => {
    window.localStorage.setItem(STORAGE_KEY, status)
    updateConsent(status)
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          We use cookies to understand site traffic and improve your experience. Choose
          "Accept" to allow analytics cookies, or "Reject" to browse with analytics
          cookies disabled.
        </p>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" size="sm" onClick={() => handleChoice("denied")}>
            Reject
          </Button>
          <Button size="sm" onClick={() => handleChoice("granted")}>
            Accept
          </Button>
        </div>
      </div>
    </div>
  )
}

declare global {
  interface Window {
    dataLayer: any[]
    gtag: (...args: any[]) => void
  }
}